import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { Database, Code, Laptop, ExternalLink, Rocket } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const Projects = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [filter, setFilter] = useState('all');
  const showWebapps = filter === 'webapps';

  const projects = [
    {
      id: 1,
      category: 'data',
      icon: Database,
      title: language === 'de' ? 'BI-Lösung KVNO' : 'BI Solution KVNO',
      company: 'Kassenärztliche Vereinigung Nordrhein',
      desc: language === 'de' 
        ? 'Entwicklung eines umfassenden Dashboard-Systems zur Analyse von Versorgungsengpässen mit Power BI und Data Warehouse Integration.'
        : 'Development of a comprehensive dashboard system for analyzing healthcare supply bottlenecks with Power BI and Data Warehouse integration.',
      tech: ['Power BI', 'Data Warehouse', 'SQL', 'PostgreSQL']
    },
    {
      id: 2,
      category: 'data',
      icon: Database,
      title: language === 'de' ? 'Healthcare BI Platform' : 'Healthcare BI Platform',
      company: 'GFO IT-Service',
      desc: language === 'de'
        ? 'Skalierbare BI-Architektur mit automatisierten Datenflüssen für strategische Entscheidungsunterstützung im Gesundheitssektor.'
        : 'Scalable BI architecture with automated data flows for strategic decision support in the healthcare sector.',
      tech: ['Apache Airflow', 'Metabase', 'PostgreSQL', 'Python']
    },
    {
      id: 3,
      category: 'web',
      icon: Code,
      title: language === 'de' ? 'Web-Anwendungsentwicklung' : 'Web Application Development',
      company: 'Various Projects',
      desc: language === 'de'
        ? 'Fullstack-Entwicklung moderner Web-Anwendungen mit React, FastAPI und responsivem Design.'
        : 'Fullstack development of modern web applications with React, FastAPI, and responsive design.',
      tech: ['React', 'Python', 'FastAPI', 'Tailwind CSS']
    },
    {
      id: 4,
      category: 'it',
      icon: Laptop,
      title: language === 'de' ? 'IT Infrastruktur-Projekt' : 'IT Infrastructure Project',
      company: 'Fastweb S.p.a.',
      desc: language === 'de'
        ? 'Planung und Entwicklung von Metropolitan Area Networks mit Glasfasertechnologie.'
        : 'Planning and development of Metropolitan Area Networks with fiber optic technology.',
      tech: ['Network Design', 'Project Management', 'Infrastructure']
    }
  ];

  const filteredProjects = filter === 'all' || filter === 'webapps'
    ? projects
    : projects.filter(p => p.category === filter);

  const ongoingProjects = [
    {
      id: 'snaprooms',
      name: 'SnapRooms',
      href: 'https://snaprooms.app/',
      repoHref: 'https://github.com/sbardella77/fotowebApp',
      status: t.projects.ongoing.statusLive,
      actionLabel: t.projects.ongoing.openWebsite,
      description: language === 'de'
        ? 'SaaS-Fotogalerie für Events: QR-Code teilen, Gäste laden Fotos ohne App/Account hoch. Organizer verwalten alles im Dashboard.'
        : 'SaaS photo-sharing platform for events: share a QR/link, guests upload from the browser (no app/account). Organizer dashboard included.',
      tech: ['Next.js', 'Prisma', 'PostgreSQL', 'Stripe', 'Vercel Blob']
    },
    {
      id: 'baucash',
      name: 'BauCash',
      href: 'https://baucash-guard.vercel.app/',
      repoHref: 'https://github.com/sbardella77/Incasssi-Edili',
      status: t.projects.ongoing.statusInProgressDemo,
      actionLabel: t.projects.ongoing.openDemo,
      description: language === 'de'
        ? 'MVP für Handwerksbetriebe: XRechnung/UBL Upload, deterministische Validierung, verständliche Fehlermeldungen, Fälligkeiten & Mahnungen mit Audit-Trail.'
        : 'MVP for tradespeople: XRechnung/UBL upload, deterministic validation, plain-language errors, due-date tracking, and human-approved reminders with an audit trail.',
      tech: ['React', 'FastAPI', 'PostgreSQL', 'Tailwind', 'Claude API']
    },
    {
      id: 'tokensentinel',
      name: 'TokenSentinel',
      href: 'https://tokensentinel.app/',
      repoHref: 'https://github.com/Token-Agent/Token-Sentinel',
      status: t.projects.ongoing.statusInProgressDemo,
      actionLabel: t.projects.ongoing.openDemo,
      description: language === 'de'
        ? 'Multi-Tenant Spend Guard für AI APIs: reserviert Worst-Case-Budget atomar in Postgres, reconciled Usage nach dem Call, immutable Audit-History.'
        : 'Multi-tenant spend guard for AI APIs: atomically reserves worst-case budget in Postgres, reconciles actual usage, and keeps an immutable audit history.',
      tech: ['Next.js', 'TypeScript', 'PostgreSQL', 'Clerk', 'AWS KMS']
    }
  ];

  return (
    <section id="projects" className="brutalist-section">
	      <div className="brutalist-container">
		        <div data-reveal="out" className="grid md:grid-cols-[0.9fr_1.1fr] gap-10 items-start">
	          <div>
	            <p className="brutalist-kicker">{t.projects.title}</p>
	            <h2 className="brutalist-title mt-4">
	              {showWebapps ? t.projects.ongoing.title : t.projects.subtitle}
	            </h2>
	            <p className="brutalist-subtitle mt-4">
	              {showWebapps ? t.projects.ongoing.subtitle : t.projects.description}
	            </p>
	          </div>

	          <div>
	            <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label={t.projects.title}>
	              <Button
	                onClick={() => setFilter('all')}
	                variant={filter === 'all' ? 'default' : 'outline'}
	                size="sm"
	              >
                {t.projects.filter.all}
              </Button>
              <Button
                onClick={() => setFilter('data')}
                variant={filter === 'data' ? 'default' : 'outline'}
                size="sm"
              >
                {t.projects.filter.data}
              </Button>
              <Button
                onClick={() => setFilter('web')}
                variant={filter === 'web' ? 'default' : 'outline'}
                size="sm"
              >
                {t.projects.filter.web}
              </Button>
	              <Button
	                onClick={() => setFilter('webapps')}
	                variant={filter === 'webapps' ? 'default' : 'outline'}
	                size="sm"
	              >
	                {t.projects.filter.webapps}
	              </Button>
              <Button
                onClick={() => setFilter('it')}
                variant={filter === 'it' ? 'default' : 'outline'}
                size="sm"
              >
                {t.projects.filter.it}
              </Button>
            </div>
          </div>
        </div>

	        {!showWebapps && (
	          <div className="mt-12 grid md:grid-cols-2 gap-4">
	            {filteredProjects.map((project, index) => {
	              const Icon = project.icon;
	              return (
	                <div
	                  key={project.id}
	                  data-reveal="out"
	                  style={{ '--reveal-delay': `${index * 60}ms` }}
	                  className="brutalist-card p-6 transition-colors duration-100 ease-linear hover:bg-muted hover:border-accent"
	                >
	                  <div className="flex items-start gap-4">
	                    <div className="h-12 w-12 border border-foreground bg-accent text-accent-foreground flex items-center justify-center flex-shrink-0">
	                      <Icon className="w-6 h-6" />
	                    </div>
	                    <div className="min-w-0">
	                      <h3 className="font-mono tracking-[0.08em] text-xl font-semibold">
	                        {project.title}
	                      </h3>
	                      <p className="mt-1 font-medium text-muted-foreground">{project.company}</p>
	                      <p className="mt-4 text-muted-foreground leading-relaxed">{project.desc}</p>

	                      <div className="mt-5 flex flex-wrap gap-2">
	                        {project.tech.map((tech, idx) => (
	                          <span
	                            key={idx}
	                            className="font-mono text-xs tracking-[0.14em] border border-border px-3 py-1"
	                          >
	                            {tech}
	                          </span>
	                        ))}
	                      </div>
	                    </div>
	                  </div>
	                </div>
	              );
	            })}
	          </div>
	        )}

	        {filter === 'all' && (
	          <div className="mt-16 border-t border-border pt-10">
	            <h3 className="mt-4 font-mono text-3xl md:text-4xl font-semibold tracking-tight">
	              {t.projects.ongoing.title}
	            </h3>
	            <p className="mt-4 text-muted-foreground text-lg leading-relaxed max-w-3xl">
	              {t.projects.ongoing.subtitle}
	            </p>

	            <div className="mt-10 grid md:grid-cols-3 gap-4">
	              {ongoingProjects.map((project, index) => (
	                <div
	                  key={project.id}
	                  data-reveal="out"
	                  style={{ '--reveal-delay': `${index * 70}ms` }}
	                  className="brutalist-card p-6 transition-colors duration-100 ease-linear hover:bg-muted hover:border-accent flex flex-col h-full"
	                >
	                  <div className="flex flex-wrap items-start justify-between gap-4">
	                    <div className="flex items-start gap-4 min-w-0">
	                      <div className="h-12 w-12 border border-foreground bg-accent text-accent-foreground flex items-center justify-center flex-shrink-0">
	                        <Rocket className="w-6 h-6" />
	                      </div>
	                      <div className="min-w-0">
	                        <h4 className="font-mono tracking-[0.08em] text-lg font-semibold">
	                          {project.name}
	                        </h4>
	                        <p className="mt-2 text-xs text-muted-foreground font-mono tracking-[0.12em] break-words">
	                          {project.href.replace(/^https?:\/\//, '').replace(/\/$/, '')}
	                        </p>
	                      </div>
	                    </div>

	                    <Badge variant="outline" className="ml-auto max-w-full text-center leading-tight">
	                      {project.status}
	                    </Badge>
	                  </div>

	                  <div className="mt-4 h-px bg-border" />

	                  <p className="mt-4 text-muted-foreground leading-relaxed">{project.description}</p>

	                  <div className="mt-5 flex flex-wrap gap-2">
	                    {project.tech.map((tech, idx) => (
	                      <span
	                        key={idx}
	                        className="font-mono text-xs tracking-[0.14em] border border-border px-3 py-1"
	                      >
	                        {tech}
	                      </span>
	                    ))}
	                  </div>

	                  <div className="mt-auto pt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
	                    <Button asChild variant="outline" size="sm" className="sm:col-span-2">
	                      <a href={project.href} target="_blank" rel="noreferrer">
	                        {project.actionLabel}
	                        <ExternalLink className="ml-2 w-4 h-4" />
	                      </a>
	                    </Button>
	                  </div>
	                </div>
	              ))}
	            </div>
	          </div>
	        )}

	        {filter === 'webapps' && (
	          <div className="mt-12 grid md:grid-cols-3 gap-4">
	            {ongoingProjects.map((project, index) => (
	              <div
	                key={project.id}
	                data-reveal="out"
	                style={{ '--reveal-delay': `${index * 70}ms` }}
	                className="brutalist-card p-6 transition-colors duration-100 ease-linear hover:bg-muted hover:border-accent flex flex-col h-full"
	              >
	                <div className="flex flex-wrap items-start justify-between gap-4">
	                  <div className="flex items-start gap-4 min-w-0">
	                    <div className="h-12 w-12 border border-foreground bg-accent text-accent-foreground flex items-center justify-center flex-shrink-0">
	                      <Rocket className="w-6 h-6" />
	                    </div>
	                    <div className="min-w-0">
	                      <h4 className="font-mono tracking-[0.08em] text-lg font-semibold">
	                        {project.name}
	                      </h4>
	                      <p className="mt-2 text-xs text-muted-foreground font-mono tracking-[0.12em] break-words">
	                        {project.href.replace(/^https?:\/\//, '').replace(/\/$/, '')}
	                      </p>
	                    </div>
	                  </div>

	                  <Badge variant="outline" className="ml-auto max-w-full text-center leading-tight">
	                    {project.status}
	                  </Badge>
	                </div>

	                <div className="mt-4 h-px bg-border" />

	                <p className="mt-4 text-muted-foreground leading-relaxed">{project.description}</p>

	                <div className="mt-5 flex flex-wrap gap-2">
	                  {project.tech.map((tech, idx) => (
	                    <span
	                      key={idx}
	                      className="font-mono text-xs tracking-[0.14em] border border-border px-3 py-1"
	                    >
	                      {tech}
	                    </span>
	                  ))}
	                </div>

	                <div className="mt-auto pt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
	                  <Button asChild variant="outline" size="sm" className="sm:col-span-2">
	                    <a href={project.href} target="_blank" rel="noreferrer">
	                      {project.actionLabel}
	                      <ExternalLink className="ml-2 w-4 h-4" />
	                    </a>
	                  </Button>
	                </div>
	              </div>
	            ))}
	          </div>
	        )}
      </div>
    </section>
  );
};

export default Projects;
